// Catalog.ID Client — The ORGANIZATION bitcash wallet entry (`bitcash_wallet`)
// ============================================================================
// An ORGANIZATION's bitcash wallet key in the organization's key bag
// (registration-wallets §4.8, D14). The entropy ceremony mints it (§4.9), the
// org finalize appends it (§4.5), and the print step releases the org
// voucher's bits into it. Everything else about the wallet — its balance, its
// receive addresses, its spending tokens, the locks that funded it — lives on
// the Worker and is read WITH the key. This entry holds the key and the little
// that cannot be read back from it.
//
// ── PERSONAL WALLETS ARE NOT HERE ANY MORE ─────────────────────────────────
// Since cid-encrypted-storage §7.2 (2026-09-17) a personal or delegate wallet
// is a row in the account's `cid/bitcash-wallets` blob, encoded by
// `crypto/bitcash-wallets-doc.ts`. D5 was relaxed the same day: a personal
// wallet key is PIN-level, readable on any device the member has unlocked, and
// never a bag entry. This module is what the ORGANIZATION path still uses,
// because an organization has no derive root its delegates can use (§5.1), so
// its wallet stays an entitled org key reached through the key wraps.
//
// One custody rule, and it is why the id is still in the vocabularies:
//
//   bag-only     no `accountpublickey` row (the `BAG_ONLY_KEY_IDS` list in
//                keybag.ts, mirrored server-side). A bitcash key has no public
//                half to publish, and a row would link the account to the
//                wallet for anyone reading the public key surface — the
//                `xrpl_master` argument, with money attached.
//
// `keyIndex` is the WALLET SLOT, not a generation: 0 is the registration
// wallet, an import or a later mint takes n+1, and an abandoned wallet is marked
// `revoked` rather than deleted, so a slot is never reused (the bag's unique
// constraint is `(accountid, keyid, keyindex)`). The blob document's `slot`
// means exactly the same thing, deliberately: the grant-policy id
// `bitcash_wallet#<slot>` is shared by both custody paths.
//
// Wire contract: `privateKey` is base64 of the RFC 8785 canonical JSON of
// {@link BitcashWalletEntryData} — the same serializer `recovery_kit` and the
// signed invitation document use. Canonical because the entry federates and must
// decode byte-identically on every node and in every later build.

import { canonicalize, type CanonicalValue } from '@catalog-canonical-json';
import * as aes from './aes';
import { toCompact, verifyWalletKey } from './bitcash';
import { type KeyBag, type KeyBagEntry } from './keybag';

/** Key-bag slot the wallet keys live in. Bag-only AND never device-stored. */
export const BITCASH_WALLET_KEY_ID = 'bitcash_wallet';

/** Algorithm tag, named for what the material IS — a bitcash CASH key —
 *  following the `recovery_kit` / `SSS-GF256` precedent. Mirrored server-side in
 *  `KnownKeyTypes`. */
export const BITCASH_WALLET_ALGORITHM = 'BITCASH-CASH';

/** Longest label a wallet may carry. Long enough for "Stichting Voorbeeld —
 *  operations", short enough that a pane can render a column of them. */
export const BITCASH_WALLET_LABEL_MAX = 64;

/** Where the key came from. `registration` is the ceremony's; `import` is a
 *  pasted key (Settings, *Add wallet…*); `settings` is a later mint. */
export type BitcashWalletSource = 'registration' | 'import' | 'settings';

const SOURCES: readonly BitcashWalletSource[] = ['registration', 'import', 'settings'];

/** The entry's plaintext. Field names and types ARE the wire contract. */
export interface BitcashWalletEntryData {
    /** Format version. Only 1 exists; anything else is refused on decode. */
    v: 1;
    /** The 24-character COMPACT wallet key. Stored compact, printed and pasted
     *  in the 34-character display form. There is no operator field: a code
     *  carries no operator (docs/cid-governance.md M13; Bitcash Ledger
     *  Specification v2 §2.4), so nothing is stored or checked beside it. */
    key: string;
    /** What the member calls this wallet. May be empty. */
    label: string;
    /** Unix SECONDS. */
    createdAt: number;
    source: BitcashWalletSource;
}

// ── encode / decode ─────────────────────────────────────────────────────────

/** Base64 of the canonical JSON — what goes into the bag entry's `privateKey`.
 *  A display-form key is folded to compact on the way in. */
export function encodeBitcashWalletEntry(data: BitcashWalletEntryData): string {
    const normalised = validate(data);
    return aes.toBase64(canonicalize(normalised as unknown as CanonicalValue));
}

/**
 * Read an entry back. Every structural promise the format makes is re-checked
 * here, because this is the only gate between a bag entry — which any node in
 * the federation may have written — and a UI that will offer to SPEND what it
 * names.
 */
export function decodeBitcashWalletEntry(privateKeyB64: string): BitcashWalletEntryData {
    let parsed: unknown;
    try {
        parsed = JSON.parse(aes.decodeUtf8(aes.fromBase64(privateKeyB64)));
    } catch {
        throw new Error('The bitcash wallet entry in your key bag is unreadable');
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        throw new Error('The bitcash wallet entry in your key bag is unreadable');
    }
    const o = parsed as Record<string, unknown>;
    if (o.v !== 1) {
        throw new Error(`Unsupported bitcash wallet entry version: ${String(o.v)}`);
    }
    return validate({
        v: 1,
        key: asString(o.key, 'key'),
        label: asString(o.label, 'label'),
        createdAt: asInteger(o.createdAt, 'createdAt'),
        source: asString(o.source, 'source') as BitcashWalletSource,
    });
}

/** Check everything and return the canonical shape (key folded to compact). */
function validate(data: BitcashWalletEntryData): BitcashWalletEntryData {
    if (data.v !== 1) {
        throw new Error(`Unsupported bitcash wallet entry version: ${String(data.v)}`);
    }
    // A FUND address and a LOCK key both fail this deliberately: the bag holds
    // the spending key, and only that (§4.8).
    if (typeof data.key !== 'string' || !verifyWalletKey(data.key)) {
        throw new Error('The bitcash wallet entry does not hold a valid wallet key');
    }
    const key = toCompact(data.key);
    if (typeof data.label !== 'string' || data.label.length > BITCASH_WALLET_LABEL_MAX) {
        throw new Error(`A wallet label is at most ${BITCASH_WALLET_LABEL_MAX} characters`);
    }
    if (!Number.isInteger(data.createdAt) || data.createdAt < 0) {
        throw new Error('The bitcash wallet entry has an invalid creation time');
    }
    if (!SOURCES.includes(data.source)) {
        throw new Error(`Unknown bitcash wallet entry source: ${String(data.source)}`);
    }
    return { v: 1, key, label: data.label, createdAt: data.createdAt, source: data.source };
}

// ── the bag half ────────────────────────────────────────────────────────────

/** Wrap the plaintext into the key-bag entry shape the bag writes expect.
 *  `keyIndex` is the wallet SLOT (0 = the registration wallet). */
export function bitcashWalletBagEntry(
    data: BitcashWalletEntryData, keyIndex = 0, status: 'active' | 'revoked' = 'active',
): KeyBagEntry {
    return {
        keyId: BITCASH_WALLET_KEY_ID,
        algorithm: BITCASH_WALLET_ALGORITHM,
        privateKey: encodeBitcashWalletEntry(data),
        keyIndex,
        status,
    };
}

/**
 * Every ACTIVE wallet in the bag, in slot order. Unlike the single-valued slots
 * (`getKeyBagEntry` takes the highest `keyIndex`), a bag may legitimately carry
 * many wallets at once — that is D3, and the Settings pane lists them all.
 */
export function activeBitcashWalletEntries(bag: KeyBag): KeyBagEntry[] {
    return bag.entries
        .filter(e => e.keyId === BITCASH_WALLET_KEY_ID && (e.status ?? 'active') === 'active')
        .sort((a, b) => (a.keyIndex ?? 0) - (b.keyIndex ?? 0));
}

/** The slot the NEXT wallet takes: one past the highest present, REVOKED
 *  wallets included, so a new wallet never collides with the bag's
 *  `(accountid, keyid, keyindex)` unique constraint. */
export function nextBitcashWalletKeyIndex(bag: KeyBag): number {
    const indexes = bag.entries
        .filter(e => e.keyId === BITCASH_WALLET_KEY_ID)
        .map(e => e.keyIndex ?? 0);
    return indexes.length === 0 ? 0 : Math.max(...indexes) + 1;
}

// ── helpers ─────────────────────────────────────────────────────────────────

function asString(v: unknown, field: string): string {
    if (typeof v !== 'string') {
        throw new Error(`Bitcash wallet entry field '${field}' must be a string`);
    }
    return v;
}

function asInteger(v: unknown, field: string): number {
    if (typeof v !== 'number' || !Number.isInteger(v)) {
        throw new Error(`Bitcash wallet entry field '${field}' must be an integer`);
    }
    return v;
}
